import React, { useEffect, useState } from "react";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import PageHeader from "src/components/common/PageHeader";
import Tabs from "src/components/ui/Tabs";
import { Badge } from "../../../components/ui/Badge";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantActions from "src/redux/slices/tenantSlice";
import { formatDateTime } from "src/utils/date-time";
import { formatAddress } from "src/utils/formatAddress";
import { useParams } from "react-router-dom";
import { Tenant, TenantPlanSnapshot } from "src/types/tenant.types";
import { TenantUsersList } from "./TenantUsersPage";
import ExtendPlanModal from "src/components/modals/ExtendPlanModal";
import RolesAndPermissionsTab from "src/components/cards/RolesAndPermissionsTab";
import TenantPlanDetails from "src/components/cards/TenantPlanDetails";

const tabs = [
  {
    label: "Plan Details",
    value: "plan-details",
  },
  {
    label: "Roles & Permissions",
    value: "roles-and-permissions",
  },
  {
    label: "Users List",
    value: "users-list",
  },
];

const TenantDetailsPage: React.FC = () => {
  const [extendPlanOpen, setExtendPlanOpen] = useState(false);
  const [tenantDetails, setTenantDetails] = useState<Tenant | null>(null);
  const [activeTab, setActiveTab] = useState("plan-details");
  const params = useParams();

  const { tenantList } = useAppSelector((state) => state.tenant);
  const dispatch = useAppDispatch();

  useEffect(() => {
    getTenantList();
  }, []);

  useEffect(() => {
    if (tenantList?.data) {
      console.log(tenantList?.data);
      let tenantDetails = tenantList?.data?.data?.filter(
        (tenant) => tenant?._id === params?.tenantId
      );

      setTenantDetails(tenantDetails[0]);
    }
  }, [tenantList]);

  const getTenantList = () =>
    dispatch(tenantActions.getTenantsListRequest({ page: 1, limit: 10 }));

  const handleExtendPlanClose = () => {
    setExtendPlanOpen(false);
  };

  const handleExtendPlanSuccess = () => {
    setExtendPlanOpen(false);
    getTenantList();
  };

  const getActivePlan = (plans: TenantPlanSnapshot[] | undefined) => {
    if (plans?.length) {
      const now = new Date();
      const sortedPlans = [...plans].sort((a, b) => {
        const aDate = a.planStartsAt || a.createdAt || "";
        const bDate = b.planStartsAt || b.createdAt || "";
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      });
      return (
        sortedPlans.find((plan) =>
          plan.planEndsAt ? new Date(plan.planEndsAt) >= now : false
        ) || sortedPlans[0]
      );
    }
    return null;
  };

  const activePlan = getActivePlan(tenantDetails?.plans);
  const getPlanStatus = (planEndsAt?: string) => {
    if (!planEndsAt) return "active";
    return new Date(planEndsAt) < new Date() ? "expired" : "active";
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: "Tenant Details | Invyntra",
        description:
          "Track and manage all tenant details with comprehensive information.",
        keywords: "tenants, details, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Header */}
        <PageHeader
          title={tenantDetails?.tenantName || "Tenant Details"}
          showBack
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Tenants", path: "/admin/tenants/list" },
            { label: tenantDetails?.tenantName || "", path: "" },
          ]}
        />

        <div className="bg-white border rounded-xl p-4 shadow-sm space-y-2">
          <h3 className="mb-5 text-lg font-semibold text-gray-800 lg:mb-7">
            Tenant Details
          </h3>
          <div className="text-sm text-gray-600 flex items-center gap-2">
            <div className="font-semibold">Tenant Logo:</div>{" "}
            {tenantDetails?.tenantLogo}
          </div>
          <div className="text-sm text-gray-600 flex items-center gap-2">
            <div className="font-semibold">Tenant Name:</div>{" "}
            {tenantDetails?.tenantName}
          </div>
          <div className="text-sm text-gray-600 flex items-center gap-2">
            <div className="font-semibold">Tenant Email:</div>{" "}
            {tenantDetails?.tenantEmail}
          </div>
          <div className="text-sm text-gray-600 flex items-center gap-2">
            <div className="font-semibold">Tenant Mobile:</div>{" "}
            {tenantDetails?.tenantMobile || "-"}
          </div>
          <div className="text-sm text-gray-600 flex items-center gap-2">
            <div className="font-semibold">Tenant Address:</div>{" "}
            {formatAddress({
              addressLine1: tenantDetails?.tenantAddressLine1,
              addressLine2: tenantDetails?.tenantAddressLine2,
              city: tenantDetails?.tenantCity,
              state: tenantDetails?.tenantState,
              pincode: tenantDetails?.tenantPincode,
            })}
          </div>
          <div className="text-sm text-gray-600 flex items-center gap-2">
            <div className="font-semibold">Company Size:</div>{" "}
            {tenantDetails?.tenantCompanySize}
          </div>
          <div className="text-sm text-gray-600 flex items-center gap-2">
            <div className="font-semibold">Status:</div>{" "}
            <Badge
              text={tenantDetails?.tenantStatus || ""}
              color={
                tenantDetails?.tenantStatus === "active"
                  ? "green"
                  : tenantDetails?.tenantStatus === "draft"
                  ? "yellow"
                  : "red"
              }
            />
          </div>
          <div className="text-sm text-gray-600 flex items-center gap-2">
            <div className="font-semibold">Joined On:</div>{" "}
            {formatDateTime(tenantDetails?.createdAt)}
          </div>
        </div>

        <Card>
          <Tabs
            tabs={tabs}
            onChange={setActiveTab}
            activeTab={activeTab}
            className="border-b border-gray-200"
          />

          <div className="pt-4">
            {activeTab === "plan-details" && (
              <TenantPlanDetails
                tenantDetails={tenantDetails}
                activePlan={activePlan}
                onExtendPlan={() => setExtendPlanOpen(true)}
                getPlanStatus={getPlanStatus}
              />
            )}
            {activeTab === "roles-and-permissions" && (
              <RolesAndPermissionsTab
                tenantId={tenantDetails?._id || ""}
                isAdminAuth
              />
            )}
            {activeTab === "users-list" && (
              <TenantUsersList tenantId={tenantDetails?._id || ""} />
            )}
          </div>
        </Card>

        {extendPlanOpen && tenantDetails?._id && (
          <ExtendPlanModal
            open={extendPlanOpen}
            tenantId={tenantDetails?._id}
            onClose={handleExtendPlanClose}
            onSuccess={handleExtendPlanSuccess}
          />
        )}
      </div>
    </Layout>
  );
};

export default TenantDetailsPage;
